import { useState, FunctionComponent, useEffect } from 'react';
import styled from 'styled-components';
import { pxToRem } from 'utils/convert';
import { search, Graph, FlowchartSearch } from 'ui/workflows/flowchart';
import { IconButton, BUTTON_VARIANTS, BUTTON_SIZES } from 'ui/button';
import { ButtonProps } from 'ui/button/Button';
import { Search, ChevronLeftSmall, ChevronRightSmall, Close16, SvgIconProps } from 'ui/icons';
import { InputText } from 'ui/inputs';
import { spacing } from 'ui/theme';

type IconGhostButtonProps = ButtonProps & {
  Icon: FunctionComponent<React.PropsWithChildren<SvgIconProps>>;
};

const IconGhostButton = ({ Icon, ...props }: IconGhostButtonProps) => (
  <IconButton {...props}>
    <Icon height={16} width={16} />
  </IconButton>
);

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const SearchSuffix = styled.div`
  /** this min-width ensures the suffix width accounts for 3 digits */
  min-width: ${spacing.x4};
  text-align: right;
`;

type SearchBarProps = {
  graph: Graph | undefined;
  isPublished?: boolean;
  onSearchOpen: (s: boolean) => void;
  buttonProps?: {
    size: BUTTON_SIZES;
    variant: BUTTON_VARIANTS;
  };
};

export const SearchBar = ({
  graph,
  isPublished = false,
  onSearchOpen,
  buttonProps,
}: SearchBarProps) => {
  const [searchValue, setSearchValue] = useState<string | null>(null);

  const [searchResult, setSearchResult] = useState<FlowchartSearch | undefined>(undefined);
  const [numMatches, setNumMatches] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const updateResults = (result: FlowchartSearch | undefined = searchResult) => {
    setNumMatches(result?.numMatches ?? 0);
    setSelectedIndex(result?.numMatches ? result?.index : -1);
  };

  const onSearchChange = (val: string) => {
    const searchInfo = graph ? search(graph, val) : undefined;
    setSearchValue(val);
    setSearchResult(searchInfo);
    updateResults(searchInfo);
  };

  // clears search results, then closes bar
  const closeSearch = () => {
    onSearchChange('');
    setSearchValue(null);
    onSearchOpen(false);
  };

  useEffect(() => closeSearch(), [isPublished]);

  return typeof searchValue === 'string' ? (
    <Container>
      <InputText
        id="wf-searchbar"
        data-test="wf-searchbar"
        type="search"
        value={searchValue}
        onChange={onSearchChange}
        autoFocus
        autoComplete={'off'}
        Suffix={
          searchValue ? (
            <SearchSuffix>
              {selectedIndex + 1} of {numMatches}
            </SearchSuffix>
          ) : null
        }
        width={pxToRem(283)}
      />
      <IconGhostButton
        data-test={'wf-left-search-button'}
        onClick={() => {
          searchResult?.prev?.();
          updateResults();
        }}
        Icon={ChevronLeftSmall}
        {...buttonProps}
      />
      <IconGhostButton
        data-test={'wf-right-search-button'}
        onClick={() => {
          searchResult?.next?.();
          updateResults();
        }}
        Icon={ChevronRightSmall}
        {...buttonProps}
      />
      <IconGhostButton
        data-test={'wf-close-search-button'}
        onClick={closeSearch}
        Icon={Close16}
        {...buttonProps}
      />
    </Container>
  ) : (
    <IconGhostButton
      data-test={'wf-search-button'}
      onClick={() => {
        setSearchValue('');
        onSearchOpen(true);
      }}
      Icon={Search}
      {...buttonProps}
    />
  );
};
